import { useState, useEffect } from 'react';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { SUBJECTS_THEME } from '../theme';
import { subscribeStudentQuizzes, summarizeQuizzes } from '../utils/studentQuizzes';
import { displayExamScores } from '../utils/questionBank';

/**
 * Live course + exam progress for the signed-in student.
 */
export default function useStudentProgress(currentUser) {
  const [modules, setModules] = useState([]);
  const [daysByModule, setDaysByModule] = useState({});
  const [completedLessons, setCompletedLessons] = useState({});
  const [bestScores, setBestScores] = useState({});
  const [quizzes, setQuizzes] = useState([]);
  const [extendedResponses, setExtendedResponses] = useState([]);
  const [erProgress, setErProgress] = useState({ passed: {}, cleared: {} });
  const [imageMapReadings, setImageMapReadings] = useState([]);
  const [imrProgress, setImrProgress] = useState({ passed: {}, cleared: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const dayUnsubs = {};
    const unsub = onSnapshot(
      query(collection(db, 'modules'), orderBy('order', 'asc')),
      (snap) => {
        const mods = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setModules(mods);
        setLoading(false);
        mods.forEach((mod) => {
          if (!dayUnsubs[mod.id]) {
            dayUnsubs[mod.id] = onSnapshot(
              query(collection(db, 'modules', mod.id, 'days'), orderBy('order', 'asc')),
              (dSnap) => {
                setDaysByModule((prev) => ({
                  ...prev,
                  [mod.id]: dSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
                }));
              }
            );
          }
        });
      }
    );
    return () => {
      unsub();
      Object.values(dayUnsubs).forEach((u) => u());
    };
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'extendedResponses'),
      (snap) => {
        setExtendedResponses(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((row) => !row.subject || row.subject === 'language-arts')
        );
      },
      () => setExtendedResponses([])
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'imageMapReadings'),
      (snap) => {
        setImageMapReadings(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((row) => !row.subject || row.subject === 'social-studies')
        );
      },
      () => setImageMapReadings([])
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setCompletedLessons({});
      setBestScores({});
      setQuizzes([]);
      setErProgress({ passed: {}, cleared: {} });
      setImrProgress({ passed: {}, cleared: {} });
      return undefined;
    }

    const progressUnsub = onSnapshot(doc(db, 'userProgress', currentUser.uid), (snap) => {
      const data = snap.exists() ? snap.data() : {};
      setCompletedLessons(data.completedLessons || {});
      setErProgress({
        passed: data.passedExtendedResponses || {},
        cleared: data.clearedExtendedResponses || {},
      });
      setImrProgress({
        passed: data.passedImageMapReadings || {},
        cleared: data.clearedImageMapReadings || {},
      });
    });

    const examUnsub = onSnapshot(doc(db, 'examAttempts', currentUser.uid), (snap) => {
      setBestScores(snap.exists() ? displayExamScores(snap.data()) : {});
    });
    const quizUnsub = subscribeStudentQuizzes(currentUser.uid, setQuizzes);

    return () => {
      progressUnsub();
      examUnsub();
      quizUnsub();
    };
  }, [currentUser]);

  const totalLectures = Object.values(daysByModule).reduce((sum, days) => sum + (days?.length || 0), 0);
  const completedCount = Object.keys(completedLessons).filter(
    (id) => completedLessons[id]
  ).length;
  const overallPct = totalLectures > 0 ? Math.round((completedCount / totalLectures) * 100) : 0;

  const subjects = SUBJECTS_THEME.map((subject) => {
    const subjectMods = modules.filter((m) => m.subject === subject.id);
    const total = subjectMods.reduce((sum, m) => sum + (daysByModule[m.id]?.length || 0), 0);
    const done = subjectMods.reduce(
      (sum, m) => sum + (daysByModule[m.id] || []).filter((d) => completedLessons[d.id]).length,
      0
    );
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    const best = Object.values(bestScores).reduce((top, score) => {
      const matches = score?.subject === subject.id || (!score?.subject && bestScores[subject.id] === score);
      if (!matches) return top;
      if (!top || (score.percentage || 0) > (top.percentage || 0)) return score;
      return top;
    }, bestScores[subject.id]?.subject ? null : bestScores[subject.id]);
    const examPct = best?.percentage ?? null;
    const examPassed = examPct != null && examPct >= 70;
    const erTotal = subject.id === 'language-arts' ? extendedResponses.length : 0;
    const erPassed = subject.id === 'language-arts'
      ? extendedResponses.filter((item) => erProgress.passed[item.id] || erProgress.cleared[item.id]).length
      : 0;
    const imrTotal = subject.id === 'social-studies' ? imageMapReadings.length : 0;
    const imrPassed = subject.id === 'social-studies'
      ? imageMapReadings.filter((item) => imrProgress.passed[item.id] || imrProgress.cleared[item.id]).length
      : 0;
    const modulesDetail = subjectMods.map((mod) => {
      const days = daysByModule[mod.id] || [];
      const modDone = days.filter((d) => completedLessons[d.id]).length;
      return {
        id: mod.id,
        title: mod.title,
        total: days.length,
        done: modDone,
        pct: days.length > 0 ? Math.round((modDone / days.length) * 100) : 0,
      };
    });

    return {
      ...subject,
      total,
      done,
      pct,
      examPct,
      examPassed,
      modulesDetail,
      lecturesUnlocked: total > 0 && done === total,
      erTotal,
      erPassed,
      imrTotal,
      imrPassed,
    };
  });

  const examsTaken = subjects.filter((s) => s.examPct != null).length;
  const examsPassed = subjects.filter((s) => s.examPassed).length;
  const avgExamScore = examsTaken > 0
    ? Math.round(
      subjects.filter((s) => s.examPct != null).reduce((sum, s) => sum + s.examPct, 0) / examsTaken
    )
    : null;

  const quizSummary = summarizeQuizzes(quizzes);

  return {
    loading,
    modules,
    completedLessons,
    bestScores,
    quizzes,
    quizSummary,
    extendedResponses,
    totalLectures,
    completedCount,
    overallPct,
    subjects,
    examsTaken,
    examsPassed,
    avgExamScore,
  };
}
